import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';

import { reduxForm, formValueSelector, Field } from 'redux-form';

import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Collapse from '@material-ui/core/Collapse';
import Typography from '@material-ui/core/Typography';

import Button from 'components/common/Button';
import IconButton from 'components/common/IconButton';
import IconFilter from 'components/icons/IconFilter';
import AutosuggestField from 'components/fields/AutosuggestField';
import TextField from 'components/fields/TextField';

import {
  requestFetchVendors,
  requestSearchVendors,
} from 'redux/modules/vendors';
import {
  requestFetchInventoryCategories,
  requestSearchInventoryCategories,
} from 'redux/modules/inventoryCategories';
import {
  requestFetchFacilities,
  requestSearchFacilities,
} from 'redux/modules/facilities';
import { accountLoginFail } from 'redux/modules/account';

const inventoryFilter = [
  'In Stock', // Default
  'Frequently Used',
  'Low Inventory',
  'Out Of Stock',
  'Backorder',
  'All Inventory',
];

const statusFilter = [
  'Active', // Default
  'Inactive',
  'All',
];

const styles = theme => ({
  root: {
    position: 'sticky',
    marginBottom: 16,
    zIndex: 21,
    top: 126,
    '&:before': {
      content: '""',
      position: 'absolute',
      bottom: -16,
      left: -8,
      right: -8,
      top: 0,
      zIndex: 1,
      backgroundColor: theme.palette.grey[100],
      [theme.breakpoints.down('sm')]: {
        bottom: -8,
      },
    },
    [theme.breakpoints.down('sm')]: {
      top: 72,
      marginBottom: 8,
    },
  },
  paper: {
    position: 'relative',
    zIndex: 2,
    padding: '14px 16px',
    [theme.breakpoints.down('sm')]: {
      padding: '8px 8px 0 8px',
    },
  },
  gridContainer: {
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
      alignItems: 'flex-start',
    },
  },
  gridItemActions: {
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: 8,
    },
  },
  gridItemForm: {
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      maxWidth: '100%',
      flexBasis: '100%',
    },
  },
  mobileButtonCounterContainer: {
    display: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      alignItems: 'center',
    },
  },
  buttonFilter: {
    marginLeft: -10,
    marginBottom: -14,
    marginTop: -16,
  },
  buttonFilterActive: {
    color: theme.palette.primary[500],
  },
  iconFilter: {
    width: 16,
    height: 16,
  },
  collapseContainer: {
    marginLeft: -8,
    marginRight: -8,
    paddingLeft: 8,
    paddingRight: 8,
  },
  collapseEntered: {
    overflow: 'visible',
  },
  form: {
    [theme.breakpoints.down('sm')]: {
      marginBottom: 8,
    },
  },
  gridItemShrink: {
    flexShrink: 1,
  },
  gridItemName: {
    maxWidth: '20%',
    flexBasis: '20%',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '66.66%',
      flexBasis: '66.66%',
    },
  },
  gridItemStatus: {
    maxWidth: '13%',
    flexBasis: '13%',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '33%',
      flexBasis: '33%',
    },
  },
});

function onChange(values, dispatch, props) {
  if (values['Selected Category Name']
    && values['Selected Category Name'] !== values['Category Name']) {
    dispatch(props.change('Category ID', ''));
  }

  if (values['Selected Custom']
    && values['Selected Custom'] !== values.Custom) {
    dispatch(props.change('Selected Custom', ''));
  }

  if (values['Selected Status']
    && values['Selected Status'] !== values.Status) {
    dispatch(props.change('Selected Status', ''));
  }

  if (values['Selected Vendor Name']
    && values['Selected Vendor Name'] !== values['Vendor Name']) {
    dispatch(props.change('Vendor ID', ''));
  }

  if (values['Selected Facility Name']
    && values['Selected Facility Name'] !== values['Facility Name']) {
    dispatch(props.change('Facility ID', ''));
  }

  
}

const InventoryFilter = ({
  handleSubmit,
  onCreateButtonClick,
  classes,
  change,
  actions,
  onMobileFilterBottonClick,
  mobileFilterExpanded,
  theme,
  filters,
  loading,
  count,
  items,
  ...otherProps
}) => (
  <div className={classes.root}>
    <Paper className={classes.paper}>
      <Grid
        className={classes.gridContainer}
        container
        justify="space-between"
        alignItems="center"
        spacing={0}
      >
        <Grid
          className={classes.gridItemForm}
          item
          xs={10}
        >
          <Collapse
            in={mobileFilterExpanded}
            classes={{
              container: classes.collapseContainer,
              entered: classes.collapseEntered,
            }}
            timeout={theme.transitions.duration.short}
          >
            <form
              className={classes.form}
              noValidate
              autoComplete="off"
              onSubmit={handleSubmit}
            >
              <Grid
                container
                justify="flex-start"
                alignItems="center"
                spacing={16}
                // wrap="nowrap"
              >
                <Grid className={classes.gridItemName} item xs={8} md={2}>
                  <Field
                    id="Product Name / HCPCS Code"
                    name="Product Name / HCPCS Code"
                    type="text"
                    component={TextField}
                    placeholder="Name or HCPCS Code"
                    endAdornmentClearButton
                    fullWidth
                  />
                </Grid>
                <Grid
                  item
                  xs={4}
                  md={2}
                >
                  <Field
                    id="Custom"
                    name="Custom"
                    placeholder="Filter By"
                    component={AutosuggestField}
                    fullWidth
                    errorPlacement="top"
                    listWidth="auto"
                    renderSuggestionContent={suggestion => suggestion}
                    onSuggestionSelected={(suggestion) => {
                      change('Selected Custom', suggestion);
                      return suggestion;
                    }}
                    fetchSuggestions={({ value, offset }) => (new Promise((resolve) => {
                      if (offset === 0) {
                        if (value.length > 0) {
                          resolve(inventoryFilter
                            .filter(item => item.toLowerCase().indexOf(value.toLowerCase()) !== -1));
                        } else {
                          resolve(inventoryFilter);
                        }
                      } else {
                        resolve([]);
                      }
                    }))}
                  />
                </Grid>
                <Grid
                  item
                  xs={6}
                  md={2}
                >
                  <Field
                    id="Category Name"
                    name="Category Name"
                    placeholder="Category"
                    component={AutosuggestField}
                    fullWidth
                    errorPlacement="top"
                    listWidth="auto"
                    renderSuggestionContent={suggestion => suggestion.Name}
                    renderInputContent={suggestion => suggestion.Name}
                    onSuggestionSelected={(suggestion) => {
                      change('Category ID', suggestion.ID);
                      change('Selected Category Name', suggestion.Name);
                      return suggestion.Name;
                    }}
                    fetchSuggestions={({ value, limit, offset }) => {
                      if (value === 'Uncategorized') {
                        return [{
                          ID: null,
                          Name: 'Uncategorized',
                        }];
                      }
                      return (
                        value.length > 0
                          ? requestSearchInventoryCategories({
                            filters: [
                              {
                                name: 'Name',
                                operation: 'startswith',
                                val: value,
                                case_sensitivity: false,
                              },
                            ],
                            limit,
                            offset,
                          }).then(res => [
                            {
                              ID: null,
                              Name: 'Uncategorized',
                            },
                            ...res.data,
                          ],
                          (err) => {
                            if (err.data.error === 'Need authorisation') {
                              actions.accountLoginFail();
                            }
                            return err;
                          })
                          : requestFetchInventoryCategories({
                            limit,
                            offset,
                          }).then(res => [
                            {
                              ID: null,
                              Name: 'Uncategorized',
                            },
                            ...res.data,
                          ],
                          (err) => {
                            if (err.data.error === 'Need authorisation') {
                              actions.accountLoginFail();
                            }
                            return err;
                          }));
                    }}
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <Field
                    id="Vendor Name"
                    name="Vendor Name"
                    component={AutosuggestField}
                    listWidth="auto"
                    placeholder="Vendor"
                    fullWidth
                    renderSuggestionContent={suggestion => suggestion['Vendor Name']}
                    renderInputContent={suggestion => suggestion['Vendor Name']}
                    onSuggestionSelected={(suggestion) => {
                      change('Vendor ID', suggestion.ID);
                      change('Selected Vendor Name', suggestion['Vendor Name']);
                      return suggestion['Vendor Name'];
                    }}
                    fetchSuggestions={({ value, limit, offset }) => (
                      value.length > 0
                        ? requestSearchVendors({
                          filters: [
                            {
                              name: 'Vendor Name',
                              operation: 'startswith',
                              val: value,
                              case_sensitivity: false,
                            },
                          ],
                          limit,
                          offset,
                        }).then(res => res.data,
                          (err) => {
                            if (err.data.error === 'Need authorisation') {
                              actions.accountLoginFail();
                            }
                            return err;
                          })
                        : requestFetchVendors({
                          limit,
                          offset,
                        }).then(res => res.data,
                          (err) => {
                            if (err.data.error === 'Need authorisation') {
                              actions.accountLoginFail();
                            }
                            return err;
                          })
                    )}
                  />
                </Grid>
                <Grid item xs={8} md={2}>
                  <Field
                    id="Facility Name"
                    name="Facility Name"
                    component={AutosuggestField}
                    listWidth="auto"
                    placeholder="Facility"
                    fullWidth
                    renderSuggestionContent={suggestion => suggestion['Company Info']['Company Name']}
                    renderInputContent={suggestion => suggestion['Company Info']['Company Name']}
                    onSuggestionSelected={(suggestion) => {
                      change('Facility ID', suggestion.ID);
                      change('Selected Facility Name', suggestion['Company Info']['Company Name']);
                      return suggestion['Company Info']['Company Name'];
                    }}
                    fetchSuggestions={({ value, limit, offset }) => (
                      value.length > 0
                        ? requestSearchFacilities({
                          filters: [
                            {
                              name: 'Facility Name',
                              operation: 'startswith',
                              val: value,
                              case_sensitivity: false,
                            },
                          ],
                          limit,
                          offset,
                        }).then(res => [
                          ...res.data,
                          {
                            'Company Info': {
                              'Company Name': 'Facility not assigned',
                            },
                            ID: null,
                          },
                        ],
                        (err) => {
                          if (err.data.error === 'Need authorisation') {
                            actions.accountLoginFail();
                          }
                          return err;
                        })
                        : requestFetchFacilities({
                          limit,
                          offset,
                        }).then(res => [
                          ...res.data,
                          {
                            'Company Info': {
                              'Company Name': 'Facility not assigned',
                            },
                            ID: null,
                          },
                        ],
                        (err) => {
                          if (err.data.error === 'Need authorisation') {
                            actions.accountLoginFail();
                          }
                          return err;
                        })
                    )}
                  />
                </Grid>
                <Grid
                  className={classes.gridItemStatus}
                  item 
                  xs={6}
                  md={2}
                >
                  <Field
                    id="Status"
                    name="Status"
                    placeholder="Status"
                    component={AutosuggestField}
                    fullWidth
                    errorPlacement="top"
                    listWidth="auto"
                    renderSuggestionContent={suggestion => suggestion}
                    onSuggestionSelected={(suggestion) => {
                      change('Selected Status', suggestion);
                      return suggestion;
                    }}
                    fetchSuggestions={({ value, offset }) => (new Promise((resolve) => {
                      if (offset === 0) {
                        if (value.length > 0) {
                          resolve(statusFilter
                            .filter(item => item.toLowerCase().indexOf(value.toLowerCase()) !== -1));
                        } else {
                          resolve(statusFilter);
                        }
                      } else {
                        resolve([]);
                      }
                    }))}
                  />
                </Grid>
              </Grid>
            </form>
          </Collapse>
        </Grid>
        <Grid className={classes.gridItemActions} item>
          <div className={classes.mobileButtonCounterContainer}>
            <IconButton
              onClick={onMobileFilterBottonClick}
              className={classNames(classes.buttonFilter, {
                [classes.buttonFilterActive]: filters && Object.keys(filters).length > 0,
              })}
            >
              <IconFilter className={classes.iconFilter} />
            </IconButton>
            <Typography>
              {(loading === 0 || items.length > 0) && (
                ` (${count})`
              )}
            </Typography>
          </div>
          <Button
              onClick={onCreateButtonClick}
              variant="raised"
              color="primary"
            >
              Add Product
            </Button>
        </Grid>
      </Grid>
    </Paper>
  </div>
);

InventoryFilter.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  reset: PropTypes.func.isRequired,
  onCreateButtonClick: PropTypes.func,
  initializeWithSearchView: PropTypes.bool.isRequired,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    gridItemShrink: PropTypes.string.isRequired,
  }).isRequired,
};

InventoryFilter.defaultProps = {
  onCreateButtonClick: () => {},
};

const mapStateToProps = (store, ownProps) => {
  const selector = formValueSelector(ownProps.form);
  const searchValue = selector(store, 'Product Name / HCPCS Code');
  return {
    initializeWithSearchView: Boolean(searchValue),
  };
};

export default connect(mapStateToProps)(reduxForm({
  onChange,
  onSubmit: () => {},
  initialValues: {
    Status: 'Active',
    'Selected Status': 'Active',
  },
})(
  withStyles(styles, { withTheme: true })(InventoryFilter),
));



// WEBPACK FOOTER //
// ./src/components/inventory/InventoryFilter/InventoryFilter.js